import { useState, useCallback } from 'react';
import { ShopsModel } from '../models/Shops';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ShopsApiAdapter, UpdateShopSettingsRequest } from '../adapters/ShopsApiAdapter';
import { useQueryClient } from '@tanstack/react-query';

export interface PaymentMethod {
  id: number;
  title: string;
  payment_method_id: number;
  shop_id: number;
  team_id: number;
}

export interface UseSettingsManagerReturn {
  // Estado
  linkedPaymentMethods: PaymentMethod[];
  newPaymentMethod: string;

  // Acciones
  addPaymentMethod: (title: string, paymentMethodId: number) => void;
  removePaymentMethod: (methodId: number) => void;
  updatePaymentMethods: (methods: PaymentMethod[]) => void;
  setNewPaymentMethod: (value: string) => void;
  clearNewPaymentMethod: () => void;

  // Utilidades
  initializeFromShop: (shop: ShopsModel) => void;
  getUpdatedShop: (shop: ShopsModel) => ShopsModel;

  // Categorías de métodos de pago
  getPaymentMethodsByCategory: () => {
    paidOrder: PaymentMethod[];
    bankTransfer: PaymentMethod[];
    cashOnDelivery: PaymentMethod[];
  };

  // Estado de guardado
  isSaving: boolean;
  saveSettings: (
    shop: ShopsModel,
    updatedSettings: {
      approveDefault: string;
      defaultPaymentMethod: string;
      trackingEnabled: boolean;
    },
    onSuccess?: () => void,
  ) => Promise<void>;
}

export const useSettingsManager = (): UseSettingsManagerReturn => {
  const [linkedPaymentMethods, setLinkedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('shopsModule');
  const queryClient = useQueryClient();
  const shopsApiAdapter = new ShopsApiAdapter(createHttpClient(apiClientWH));

  // Inicializar desde un shop
  const initializeFromShop = useCallback((shop: ShopsModel) => {
    setLinkedPaymentMethods(shop.linked_payment_methods || []);
    setNewPaymentMethod('');
  }, []);

  // Agregar nuevo método de pago
  const addPaymentMethod = useCallback((title: string, paymentMethodId: number) => {
    if (title.trim()) {
      const newMethod: PaymentMethod = {
        id: Date.now(), // ID temporal
        title: title.trim(),
        payment_method_id: paymentMethodId,
        shop_id: 0, // Se actualizará cuando se use
        team_id: 0, // Se actualizará cuando se use
      };

      setLinkedPaymentMethods((prev) => [...prev, newMethod]);
      setNewPaymentMethod('');
    }
  }, []);

  // Eliminar método de pago
  const removePaymentMethod = useCallback((methodId: number) => {
    setLinkedPaymentMethods((prev) => prev.filter((method) => method.id !== methodId));
  }, []);

  // Actualizar métodos de pago (para sincronización)
  const updatePaymentMethods = useCallback((methods: PaymentMethod[]) => {
    setLinkedPaymentMethods(methods);
  }, []);

  // Limpiar input de nuevo método
  const clearNewPaymentMethod = useCallback(() => {
    setNewPaymentMethod('');
  }, []);

  // Obtener shop actualizado con los métodos de pago
  const getUpdatedShop = useCallback(
    (shop: ShopsModel): ShopsModel => {
      return {
        ...shop,
        linked_payment_methods: linkedPaymentMethods,
      };
    },
    [linkedPaymentMethods],
  );

  // Dividir métodos de pago por categorías
  const getPaymentMethodsByCategory = useCallback(() => {
    const paidOrder = linkedPaymentMethods.filter((method) => method.payment_method_id === 1);
    const bankTransfer = linkedPaymentMethods.filter((method) => method.payment_method_id === 2);
    const cashOnDelivery = linkedPaymentMethods.filter((method) => method.payment_method_id === 3);

    return {
      paidOrder,
      bankTransfer,
      cashOnDelivery,
    };
  }, [linkedPaymentMethods]);

  // Guardar ajustes
  const saveSettings = useCallback(
    async (
      shop: ShopsModel,
      updatedSettings: {
        approveDefault: string;
        defaultPaymentMethod: string;
        trackingEnabled: boolean;
      },
      onSuccess?: () => void,
    ) => {
      setIsSaving(true);
      try {
        const payload: UpdateShopSettingsRequest = {
          name: shop.name,
          fulfillment: updatedSettings.trackingEnabled,
          default_payment_method: updatedSettings.defaultPaymentMethod
            ? parseInt(updatedSettings.defaultPaymentMethod, 10)
            : null,
          approval_type: parseInt(updatedSettings.approveDefault, 10),
          linked_payment_methods: linkedPaymentMethods.map((p) => ({
            id: p.id > 1_000_000_000 ? undefined : p.id,
            title: p.title,
            payment_method_id: p.payment_method_id,
          })),
        };

        const response = await shopsApiAdapter.updateShopSettings(shop.id, payload);

        if (response.success) {
          toast({
            title: t('modals.settings.toast.success.title'),
            description: response.message || t('modals.settings.toast.success.description'),
            variant: 'default',
          });
          // Invalidar caché para refrescar datos
          await queryClient.invalidateQueries({
            queryKey: ['shops'],
            exact: false,
          });
          // Ejecutar callback de éxito (ej: cerrar modal)
          onSuccess?.();
        } else {
          toast({
            title: t('modals.settings.toast.error.title'),
            description: response.message || t('toasts.error.unexpected'),
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        toast({
          title: t('modals.settings.toast.error.title'),
          description: error.message || t('toasts.error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [linkedPaymentMethods, shopsApiAdapter, t, toast, queryClient],
  );

  return {
    // Estado
    linkedPaymentMethods,
    newPaymentMethod,

    // Acciones
    addPaymentMethod,
    removePaymentMethod,
    updatePaymentMethods,
    setNewPaymentMethod,
    clearNewPaymentMethod,

    // Utilidades
    initializeFromShop,
    getUpdatedShop,

    // Categorías de métodos de pago
    getPaymentMethodsByCategory,

    // Estado de guardado
    isSaving,
    saveSettings,
  };
};
